import { FC } from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import {
  InsightsFilterV2,
  InsightsMetricOperationV2,
  InsightsFilter,
  getMetricFormat,
  getIsMetricEnumerable,
} from '@magicbrief/common';
import { useParams } from 'react-router-dom';
import {
  Popover,
  PopoverDialog,
  PopoverTrigger,
} from '@magicbrief/ui/src/components/popover';
import {
  Select,
  SelectButton,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@magicbrief/ui/src/components/select';
import { ListBox, ListBoxItem } from '@magicbrief/ui/src/components/list-box';
import { cn } from '@magicbrief/ui/src/lib/cn';
import { NumberField } from '@magicbrief/ui/src/components/number-field';
import { FieldError, Input } from '@magicbrief/ui/src/components/field';
import { DateField, DateInput, DateSegment } from 'react-aria-components';
import { fromDate, getLocalTimeZone } from '@internationalized/date';
import { startCase } from 'lodash';
import {
  useFilterFormFieldV2,
  useFilterFormV2,
} from 'src/pages/Insights/util/useFilterFormV2';
import { useI18nContext } from 'src/i18n/i18n-react';
import { AriaButton } from 'src/components/Button/Button';
import { trpc } from 'src/lib/trpc';
import ChevronDown from 'src/assets/svgicons/line/chevron-down.svg';
import { getDatePresetsForPlatform } from 'src/pages/Insights/util/getDatePresetsForPlatform';
import { InsightsFormFilterMetricOperationSelect } from './InsightsFilterMetricOperationSelect';
import { InsightsFormFilterMetricFieldSelect } from './InsightsFilterMetricFieldSelect';

type Props = {
  platform: 'facebook' | 'tiktok';
  onSaveComplete: (filters: Array<InsightsFilterV2>) => void;
  defaultValues?: Array<InsightsFilterV2>;
  customEvents: string[] | null;
  customConversions: Array<{ facebookId: string; name: string }> | null;
};

export const InsightsFilterForm: FC<Props> = ({
  onSaveComplete,
  defaultValues,
  customEvents,
  customConversions,
  platform,
}) => {
  const { handleSubmit, ...formMethods } = useFilterFormV2({
    platform,
    defaultValues: { filters: { values: defaultValues ?? [] } },
  });

  const onSubmit = handleSubmit((formValues) => {
    onSaveComplete(formValues['filters'].values);

    // void recordEvent({
    //   action: `Metric ${filter ? 'Added' : 'Edited'}`,
    //   target: 'Insights Metric',
    //   metadata: {
    //     metric: first.metric,
    //     operation: first.operation,
    //     value1:
    //       'value1' in first
    //         ? Array.isArray(first.value1)
    //           ? first.value1.join(', ')
    //           : first.value1
    //         : undefined,
    //     value2:
    //       'value2' in first
    //         ? Array.isArray(first.value2)
    //           ? first.value2.join(', ')
    //           : first.value2
    //         : undefined,
    //   },
    // });
  });

  return (
    <FormProvider handleSubmit={handleSubmit} {...formMethods}>
      <form onSubmit={onSubmit}>
        <InsightsFilterFormFields
          platform={platform}
          prefix="filters."
          customEvents={customEvents}
          customConversions={customConversions}
        />
      </form>
    </FormProvider>
  );
};

type InsightsFilterFormFieldProps = {
  prefix: string;
  platform: 'facebook' | 'tiktok';
  customEvents: string[] | null;
  customConversions: Array<{ facebookId: string; name: string }> | null;
};

export const InsightsFilterFormFields: FC<InsightsFilterFormFieldProps> = ({
  prefix,
  platform,
  customEvents,
  customConversions,
}) => {
  const { fields } = useFilterFormFieldV2(platform, prefix);

  return (
    <div>
      {fields.map((renderField, i) => {
        return (
          <div
            key={renderField.id}
            className="flex flex-auto flex-col items-center gap-2 lg:flex-row"
          >
            <span className="text-sm text-primary/60">Where</span>

            <div className="grid max-w-[600px] auto-cols-fr grid-flow-col grid-rows-1 gap-2">
              <InsightsFilterFormFieldsRow
                platform={platform}
                index={i}
                prefix={prefix}
                customConversions={customConversions}
                customEvents={customEvents}
              />
            </div>
            <AriaButton htmlType="submit">Save</AriaButton>
          </div>
        );
      })}
    </div>
  );
};

export const InsightsFilterFormFieldsRow: FC<
  InsightsFilterFormFieldProps & {
    index: number;
    platform: 'facebook' | 'tiktok';
  }
> = ({ prefix, customConversions, customEvents, index, platform }) => {
  const { onMetricChange, onOperationChange } = useFilterFormFieldV2(
    platform,
    prefix
  );
  return (
    <>
      <InsightsFormFilterMetricFieldSelect
        platform={platform}
        prefix={`${prefix}values.${index}`}
        customConversions={customConversions}
        customEvents={customEvents}
        onChange={(m) => onMetricChange(m, index)}
      />

      <InsightsFormFilterMetricOperationSelect
        platform={platform}
        prefix={`${prefix}values.${index}`}
        onChange={(e: string) =>
          onOperationChange(platform, e as InsightsMetricOperationV2, index)
        }
      />

      <InsightsFilterFormFieldValueInputs
        platform={platform}
        prefix={`${prefix}values.${index}`}
      />
    </>
  );
};

type InsightsFilterFormFieldValueInputsProps = {
  prefix: string;
  platform: 'facebook' | 'tiktok';
};

export const InsightsFilterFormFieldValueInputs: FC<
  InsightsFilterFormFieldValueInputsProps
> = ({ prefix, platform }) => {
  const { LL } = useI18nContext();
  const { control, register, watch } = useFormContext();
  const filter: InsightsFilter = watch(prefix);

  if (filter.field === 'tag') {
    return <InsightsFilterFormFieldValueInputsTagCombobox prefix={prefix} />;
  }

  const enumerable = getIsMetricEnumerable(platform, filter.field);

  if (enumerable) {
    return (
      <InsightsFilterFormFieldValueInputsCombobox
        values={Object.entries(enumerable).map(([value, label]) => ({
          value,
          label: label as string,
        }))}
        prefix={prefix}
      />
    );
  }

  const format = getMetricFormat(platform, filter.field);

  switch (format) {
    case 'currency':
    case 'numeric':
    case 'percentage': {
      if (filter.operation === 'between' || filter.operation === 'notBetween') {
        return (
          <>
            <Input
              {...register(`${prefix}.min`, { valueAsNumber: true })}
              placeholder={'Min'}
              type="number"
              step={0.01}
            />

            <Input
              {...register(`${prefix}.max`, { valueAsNumber: true })}
              placeholder={'Max'}
              type="number"
              step={0.01}
            />
          </>
        );
      }

      return (
        <Controller
          control={control}
          name={`${prefix}.value`}
          render={({ field }) => {
            const value = watch(`${prefix}.value`);

            return (
              <NumberField className="flex-1" aria-label="enter filter value">
                <Input
                  ref={field.ref}
                  name={field.name}
                  value={Number(value)}
                  onChange={(ev) => field.onChange(parseFloat(ev.target.value))}
                  onBlur={field.onBlur}
                  disabled={field.disabled}
                  placeholder="Value"
                  type="number"
                  step={0.01}
                />
                <FieldError>Required</FieldError>
              </NumberField>
            );
          }}
        />
      );
    }
    case 'text':
      return (
        <Controller
          name={`${prefix}.value`}
          control={control}
          render={({ field }) => (
            <>
              <Input
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                placeholder={'Value'}
                type="text"
              />
            </>
          )}
        />
      );
    case 'date':
      if (filter.operation === 'between' || filter.operation === 'notBetween') {
        return (
          <>
            <Controller
              name={`${prefix}.start`}
              control={control}
              render={({ field }) => {
                const defaultValue =
                  field.value && field.value instanceof Date
                    ? fromDate(field.value, getLocalTimeZone())
                    : undefined;

                return (
                  <DateField
                    ref={field.ref}
                    name={field.name}
                    onChange={(calendarDate) => {
                      if (!calendarDate) {
                        field.onChange(null);
                        return;
                      }
                      const jsDate = new Date(
                        calendarDate.year,
                        calendarDate.month - 1,
                        calendarDate.day
                      );
                      field.onChange(jsDate);
                    }}
                    onBlur={field.onBlur}
                    defaultValue={defaultValue}
                    granularity="day"
                    hideTimeZone
                    aria-label="start date input"
                  >
                    <DateInput
                      className={cn(
                        'flex h-9 w-full items-center rounded-lg border border-solid border-purple-200 bg-white px-3 py-1 text-sm text-primary shadow-sm transition-colors placeholder:text-primary/50',
                        /* Focused */
                        'focus:ring-1 focus:ring-purple-500',
                        /* Disabled */
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        /* Resets */
                        'focus:outline-none'
                      )}
                    >
                      {(segment) => (
                        <DateSegment
                          segment={segment}
                          className={cn(
                            'inline rounded p-0.5 caret-transparent outline-0 type-literal:px-0',
                            /* Placeholder */
                            'data-[placeholder]:text-primary/50',
                            /* Disabled */
                            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                            /* Focused */
                            'data-[focused]:bg-purple-100 data-[focused]:text-primary',
                            /* Type */
                            'data-[type=literal]:text-primary/50'
                          )}
                        />
                      )}
                    </DateInput>
                  </DateField>
                );
              }}
            />
            <Controller
              name={`${prefix}.end`}
              control={control}
              render={({ field }) => {
                const defaultValue =
                  field.value && field.value instanceof Date
                    ? fromDate(field.value, getLocalTimeZone())
                    : undefined;

                return (
                  <DateField
                    ref={field.ref}
                    name={field.name}
                    onChange={(calendarDate) => {
                      if (!calendarDate) {
                        field.onChange(null);
                        return;
                      }
                      const jsDate = new Date(
                        calendarDate.year,
                        calendarDate.month - 1,
                        calendarDate.day
                      );
                      field.onChange(jsDate);
                    }}
                    onBlur={field.onBlur}
                    defaultValue={defaultValue}
                    granularity="day"
                    hideTimeZone
                    aria-label="end date input"
                  >
                    <DateInput
                      className={cn(
                        'flex h-9 w-full items-center rounded-lg border border-solid border-purple-200 bg-white px-3 py-1 text-sm text-primary shadow-sm transition-colors placeholder:text-primary/50',
                        /* Focused */
                        'focus:ring-1 focus:ring-purple-500',
                        /* Disabled */
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        /* Resets */
                        'focus:outline-none'
                      )}
                    >
                      {(segment) => (
                        <DateSegment
                          segment={segment}
                          className={cn(
                            'inline rounded p-0.5 caret-transparent outline-0 type-literal:px-0',
                            /* Placeholder */
                            'data-[placeholder]:text-primary/50',
                            /* Disabled */
                            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                            /* Focused */
                            'data-[focused]:bg-purple-100 data-[focused]:text-primary',
                            /* Type */
                            'data-[type=literal]:text-primary/50'
                          )}
                        />
                      )}
                    </DateInput>
                  </DateField>
                );
              }}
            />
          </>
        );
      }

      if (filter.operation === 'withinDatePreset') {
        const datePresets = getDatePresetsForPlatform(platform);

        return (
          <Controller
            name={`${prefix}.value`}
            control={control}
            render={({ field }) => {
              const value = watch(`${prefix}.value`);

              const items = datePresets
                .filter((x) => x !== 'custom')
                .map((x) => ({
                  id: x,
                  name: LL.insights.timePeriod[
                    x as keyof typeof LL.insights.timePeriod
                  ](),
                }));

              return (
                <Select
                  className="flex-1"
                  defaultSelectedKey={value}
                  onSelectionChange={(selection) =>
                    field.onChange(selection as string)
                  }
                  aria-label="select date preset"
                >
                  <SelectButton>
                    <SelectValue className="text-ellipsis" />
                    <ChevronDown className="size-4 text-primary/50" />
                  </SelectButton>
                  <SelectContent
                    popoverClassName="min-w-[--trigger-width] w-fit"
                    items={items}
                  >
                    {(item) => (
                      <SelectItem key={item.id} id={item.id}>
                        {item.name}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              );
            }}
          />
        );
      }

      if (filter.operation === 'withinLast') {
        return (
          <>
            <Controller
              name={`${prefix}.value`}
              control={control}
              render={({ field }) => {
                const value = watch(`${prefix}.value`);

                return (
                  <NumberField
                    className="flex-1"
                    aria-label="enter date interval value"
                  >
                    <Input
                      ref={field.ref}
                      name={field.name}
                      value={Number(value)}
                      onChange={(ev) =>
                        field.onChange(parseFloat(ev.target.value))
                      }
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                      placeholder="Value"
                      type="number"
                      step={0.01}
                    />
                    <FieldError>Required</FieldError>
                  </NumberField>
                );
              }}
            />
            <Controller
              name={`${prefix}.interval`}
              control={control}
              render={({ field }) => {
                const value = watch(`${prefix}.interval`);
                const items = ['day', 'week', 'month', 'year'].map((x) => ({
                  id: x,
                  name: startCase(x),
                }));

                return (
                  <Select
                    className="flex-1"
                    defaultSelectedKey={value}
                    onSelectionChange={(selection) =>
                      field.onChange(selection as string)
                    }
                    aria-label="select date interval"
                  >
                    <SelectButton>
                      <SelectValue className="text-ellipsis" />
                      <ChevronDown className="size-4 text-primary/50" />
                    </SelectButton>
                    <SelectContent
                      popoverClassName="min-w-[--trigger-width] w-fit"
                      items={items}
                    >
                      {(item) => (
                        <SelectItem key={item.id} id={item.id}>
                          {item.name}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </>
        );
      }

      return (
        <Controller
          name={`${prefix}.value`}
          control={control}
          render={({ field }) => {
            const defaultValue =
              field.value && field.value instanceof Date
                ? fromDate(field.value, getLocalTimeZone())
                : undefined;

            return (
              <DateField
                ref={field.ref}
                name={field.name}
                onChange={(calendarDate) => {
                  if (!calendarDate) {
                    field.onChange(null);
                    return;
                  }
                  const jsDate = new Date(
                    calendarDate.year,
                    calendarDate.month - 1,
                    calendarDate.day
                  );
                  field.onChange(jsDate);
                }}
                onBlur={field.onBlur}
                defaultValue={defaultValue}
                granularity="day"
                hideTimeZone
                aria-label="date input"
              >
                <DateInput
                  className={cn(
                    'flex h-9 w-full items-center rounded-lg border border-solid border-purple-200 bg-white px-3 py-1 text-sm text-primary shadow-sm transition-colors placeholder:text-primary/50',
                    /* Focused */
                    'focus:ring-1 focus:ring-purple-500',
                    /* Disabled */
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    /* Resets */
                    'focus:outline-none'
                  )}
                >
                  {(segment) => (
                    <DateSegment
                      segment={segment}
                      className={cn(
                        'inline rounded p-0.5 caret-transparent outline-0 type-literal:px-0',
                        /* Placeholder */
                        'data-[placeholder]:text-primary/50',
                        /* Disabled */
                        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                        /* Focused */
                        'data-[focused]:bg-purple-100 data-[focused]:text-primary',
                        /* Type */
                        'data-[type=literal]:text-primary/50'
                      )}
                    />
                  )}
                </DateInput>
              </DateField>
            );
          }}
        />
      );

    default: {
      format satisfies never;
      return null;
    }
  }
};

type InsightsFilterFormFieldValueInputsCombobox = {
  values: Array<{ value: string; label: string }>;
  prefix: string;
};

const InsightsFilterFormFieldValueInputsCombobox: React.FC<
  InsightsFilterFormFieldValueInputsCombobox
> = ({ values, prefix }) => {
  const { LL } = useI18nContext();
  const { control, watch } = useFormContext();

  return (
    <Controller
      name={`${prefix}.values`}
      control={control}
      render={({ field: { onChange } }) => {
        const value: string[] = watch(`${prefix}.values`);
        const showPlaceholder = value.length === 0;
        const label = showPlaceholder
          ? LL.insights.metrics.multiSelectHint()
          : value
              .map((x) =>
                LL.insights.creativeType[
                  x as keyof typeof LL.insights.creativeType
                ]()
              )
              .join(', ');

        const items = values.map((x) => ({
          id: x.value,
          name: x.label,
        }));

        return (
          <PopoverTrigger>
            <SelectButton
              className={cn(
                'overflow-',
                showPlaceholder ? 'text-primary/50' : ''
              )}
            >
              {label}
              <ChevronDown className="size-4 text-primary/50" />
            </SelectButton>
            <Popover className="w-[250px]">
              <PopoverDialog>
                <ListBox
                  selectedKeys={value}
                  items={items}
                  selectionMode="multiple"
                  onSelectionChange={(selection) => {
                    onChange(Array.from(selection));
                  }}
                  aria-label="select enumerable value"
                >
                  {(item) => (
                    <ListBoxItem key={item.id} id={item.id}>
                      {item.name}
                    </ListBoxItem>
                  )}
                </ListBox>
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
        );
      }}
    />
  );
};

const InsightsFilterFormFieldValueInputsTagCombobox: React.FC<{
  prefix: string;
}> = ({ prefix }) => {
  const { accountUuid } = useParams();
  const tags = trpc.insightsTags.getTags.useQuery({
    insightsAdAccountFacebookUuid: accountUuid ?? '',
  });

  return (
    <InsightsFilterFormFieldValueInputsCombobox
      prefix={prefix}
      values={tags.data?.map((x) => ({ value: x.uuid, label: x.label })) ?? []}
    />
  );
};
